/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { ITooltipStyles } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import { default as React } from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { ResourceKeyCell } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ResourceKeyCell';
import { feedback } from 'src/clu/cluExternalLinks';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { CopyButton } from 'src/core/components/CopyButton';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Page } from 'src/core/components/Page';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick, logTextEntry, logToggleChange } from 'src/core/services/telemetry/eventsNamesTemplate';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { TooltipProps } from 'src/modules/labeling/components/Tooltip';

type CluProjectSettingsDto = Pick<
    CluProjectStore,
    'description' | 'isMultiLingual' | 'oosThreshold' | 'isCasingNormalized' | 'isDiacriticsAugmented'
>;

const messages = defineMessages({
    value: { id: 'clu.cluProjectSettingsPage.value', defaultMessage: 'Value' },
    saveButton: { id: 'clu.cluProjectSettingsPage.saveButton', defaultMessage: 'Save' },
    nameLabel: { id: 'clu.cluProjectSettingsPage.nameLabel', defaultMessage: 'Project Name' },
    feedback: { id: 'clu.cluProjectSettingsPage.feedback', defaultMessage: 'Give us feedback' },
    enabledToggle: { id: 'clu.cluProjectSettingsPage.enabledToggle', defaultMessage: 'Enabled' },
    pageTitle: { id: 'clu.cluProjectSettingsPage.pageTitle', defaultMessage: 'Project settings' },
    languageTitle: { id: 'clu.cluProjectSettingsPage.languageTitle', defaultMessage: 'Language' },
    experimental: { id: 'clu.cluProjectSettingsPage.experimental', defaultMessage: 'Experimental' },
    primaryKeyLabel: { id: 'clu.cluProjectSettingsPage.primaryKeyLabel', defaultMessage: 'Primary key:' },
    viewPrimaryKeyLabel: { id: 'clu.cluProjectSettingsPage.viewPrimaryKeyLabel', defaultMessage: 'Primary key ' },
    notEnabledToggle: { id: 'clu.cluProjectSettingsPage.notEnabledToggle', defaultMessage: 'Not enabled' },
    descriptionLabel: { id: 'clu.cluProjectSettingsPage.descriptionLabel', defaultMessage: 'Project description' },
    nameSectionTitle: { id: 'clu.cluProjectSettingsPage.nameSectionTitle', defaultMessage: 'Name and description' },
    goToOrchestration: { id: 'clu.cluProjectSettingsPage.goToOrchestration', defaultMessage: 'Go to orchestration' },
    oosThresholdLabel: { id: 'clu.cluProjectSettingsPage.oosThresholdLabel', defaultMessage: 'None score threshold' },
    azureResourceLabel: { id: 'clu.cluProjectSettingsPage.azureResourceLabel', defaultMessage: 'Language resource:' },
    orchestrationTitle: { id: 'clu.cluProjectSettingsPage.orchestrationTitle', defaultMessage: 'Orchestrate project' },
    multiLingualTitle: { id: 'clu.cluProjectSettingsPage.multiLingualTitle', defaultMessage: 'Multi-lingual dataset:' },
    multiLingualLabel: { id: 'clu.cluProjectSettingsPage.multiLingualLabel', defaultMessage: 'Multi-lingual dataset' },
    azureResourceTitle: { id: 'clu.cluProjectSettingsPage.azureResourceTitle', defaultMessage: 'Azure Language resource' },
    primaryLanguageTitle: { id: 'clu.cluProjectSettingsPage.primaryLanguageTitle', defaultMessage: 'Text primary language:' },
    advancedSettingsTitle: { id: 'clu.cluProjectSettingsPage.advancedSettingsTitle', defaultMessage: 'Advanced project settings:' },
    normalizeCasingLabel: {
        id: 'clu.cluProjectSettingsPage.normalizeCasingLabel',
        defaultMessage: 'Data transformation for casing'
    },
    normalizeDiacriticsLabel: {
        id: 'clu.cluProjectSettingsPage.normalizeDiacriticsLabel',
        defaultMessage: 'Data augmentation for diacritics'
    },
    casingDescription: {
        id: 'clu.cluProjectSettingsPage.casingDescription',
        defaultMessage:
            'Enable this option if you would like your model to ignore letter casing in training and prediction. (e.g. "Microsoft Corporation" vs "microsoft corporation")'
    },
    diacriticsDescription: {
        id: 'clu.cluProjectSettingsPage.diacriticsDescription',
        defaultMessage:
            'Enable this option if you would like CLU to add training data for special characters written with English keyboards. (e.g. For the French utterance "Accédez à la chaîne sportive", CLU will also include the utterance "Accedez a la chaine sportive".)'
    },
    oosThresholdErrorMsg: {
        id: 'clu.cluProjectSettingsPage.oosThresholdErrorMsg',
        defaultMessage: 'Value should be greater that or equal to 0 and less than 1'
    },
    orchestrationDescription: {
        id: 'clu.cluProjectSettingsPage.orchestrationDescription',
        defaultMessage:
            'Orchestration workflow allows you to chain multiple projects together in Language, such as orchestrating between other Conversation projects, LUIS, and QnA Maker KBs'
    },
    azureResourceDescription: {
        id: 'clu.cluProjectSettingsPage.azureResourceDescription',
        defaultMessage: `A Language resource is created for you when you create your
Language account. The associated key is used for publishing your project`
    },
    oosThresholdTooltip: {
        id: 'clu.cluProjectSettingsPage.oosThresholdTooltip',
        defaultMessage: 'If the top intent’s score is below this threshold, the top intent will be automatically detected as None'
    },
    saveProjectRunningToast: { id: 'clu.cluProjectSettingsPage.saveProjectRunningToast', defaultMessage: 'Saving project settings' },
    saveProjectCompletedToast: {
        id: 'clu.cluProjectSettingsPage.saveProjectCompletedToast',
        defaultMessage: 'Saved project settings successfully'
    },
    saveProjectFailedToast: {
        id: 'clu.cluProjectSettingsPage.saveProjectFailedToast',
        defaultMessage: 'Failed to save project settings'
    }
});

const rBACTooltipStyles = classNamesFunction<TooltipProps, ITooltipStyles>()({ root: { width: '400px' } });

const isFormDirty = (currentProject: CluProjectStore, currentForm: CluProjectSettingsDto) =>
    currentForm.isMultiLingual !== currentProject.isMultiLingual ||
    currentForm.description !== currentProject.description ||
    currentForm.oosThreshold !== currentProject.oosThreshold ||
    currentForm.isCasingNormalized !== currentProject.isCasingNormalized ||
    currentForm.isDiacriticsAugmented !== currentProject.isDiacriticsAugmented;

export const CluProjectSettingsPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const updateTracker = useProgressTracker();
    const { serviceName } = getPortalFromUrl();
    const { isLargeOrGreater } = useResponsiveMode();
    const { rootStore, dispatcher } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canListResourceKeys } = mapUserRoleToUserActions(userRole);

    const currentProject = rootStore.currentCluProjectStore;
    const currentResource = rootStore.azureStore.session.csAccountStore;
    const isConversationProject = currentProject.projectType === 'Conversation';

    const [oosThresholdText, setOosThresholdText] = React.useState<string>(`${currentProject.oosThreshold ?? 0}`);
    const { formState, formStateDto, updateFormState } = useFormState<CluProjectSettingsDto>({
        description: [currentProject.description],
        isMultiLingual: [currentProject.isMultiLingual, FormValidators.createRequiredValidator()],
        oosThreshold: [
            currentProject.oosThreshold ?? 0,
            FormValidators.createNumberRangeValidator({ range: [0, 1], maxMode: 'exclude', errorMessage: messages.oosThresholdErrorMsg })
        ],
        isCasingNormalized: [
            currentProject.isCasingNormalized,
            FormValidators.createRequiredValidator()
        ],
        isDiacriticsAugmented: [
            currentProject.isDiacriticsAugmented,
            FormValidators.createRequiredValidator()
        ]
    });

    const projectLanguage =
        (isConversationProject ? rootStore.cluConversationCultureStoreHouse : rootStore.cluOrchestrationCultureStoreHouse).items.find(
            l => l.code === currentProject.culture
        )?.name ?? currentProject.culture;

    const updateCluProject = () => {
        const { description, isMultiLingual, oosThreshold, isCasingNormalized, isDiacriticsAugmented } = formStateDto;

        if (!formState.form.valid || !isFormDirty(currentProject, formStateDto)) {
            return;
        }

        if (currentProject.oosThreshold !== oosThreshold) {
            logTextEntry(serviceName, 'Settings', 'OOSEntry', currentProject.id);
        }

        dispatcher.dispatch('updateCluProject', {
            description,
            toastData: {
                failed: { title: localize(messages.saveProjectFailedToast) },
                running: { title: localize(messages.saveProjectRunningToast) },
                completed: { title: localize(messages.saveProjectCompletedToast) }
            },
            isMultiLingual,
            oosThreshold,
            isCasingNormalized,
            isDiacriticsAugmented,
            tracker: updateTracker
        });
    };

    const resetForm = () => {
        updateFormState({
            description: currentProject.description,
            isMultiLingual: currentProject.isMultiLingual,
            oosThreshold: currentProject.oosThreshold ?? 0,
            isCasingNormalized: currentProject.isCasingNormalized,
            isDiacriticsAugmented: currentProject.isDiacriticsAugmented
        });
        setOosThresholdText(String(currentProject.oosThreshold ?? 0));
    };

    const areButtonsEnabled = () => {
        if (updateTracker.isRunning) {
            return false;
        }

        if (isFormDirty(currentProject, formStateDto)) {
            return true;
        }

        return false;
    };

    const items: ICommandBarItemProps[] = [
        {
            key: 'save',
            onClick: updateCluProject,
            disabled: !areButtonsEnabled(),
            iconProps: { iconName: 'Save' },
            text: localize(messages.saveButton)
        },
        {
            key: 'cancel',
            onClick: resetForm,
            disabled: !areButtonsEnabled(),
            iconProps: { iconName: 'Cancel' },
            text: localize(globalMessages.cancel)
        }
    ];

    const renderTitles = (title: string) => (
        <LuisText variant="mediumPlus" fontWeight={600}>
            {title}
        </LuisText>
    );

    const renderLabeledText = (label: string, text: string | React.ReactNode) => (
        <Stack horizontal={isLargeOrGreater} verticalAlign="center">
            <Label styles={{ root: { width: '250px' } }}>{label}</Label>
            <LuisText>{text}</LuisText>
        </Stack>
    );

    return (
        <Page size="container-fluid" pageName="CluProjectSettingsPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} />

            <UnsavedChangesPrompt
                isPageDirty={isFormDirty(currentProject, formStateDto)}
                promptPath={routeTable.clu.projectSettingsPage(currentProject.name)}
            />

            <Stack tokens={{ childrenGap: 25 }}>
                <Stack>
                    <LuisCommandBar items={items} />
                    <LuisSeparator />
                </Stack>

                <Stack tokens={{ childrenGap: 15 }}>
                    {renderTitles(localize(messages.nameSectionTitle))}

                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
                        <LuisTextField
                            disabled
                            styles={{ root: { width: 400 } }}
                            value={currentProject.name}
                            label={localize(messages.nameLabel)}
                        />

                        <CopyButton textToCopy={currentProject.name} label={localize(messages.nameLabel)} />
                    </Stack>

                    <RBACTooltip
                        hidden={canWrite}
                        styles={rBACTooltipStyles}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="projectDescriptionFieldLanguageToolTip"
                        componentSurroundedByToolTip={
                            <LuisTextField
                                multiline
                                disabled={!canWrite}
                                onEnterKeyUp={updateCluProject}
                                styles={{ root: { width: isLargeOrGreater ? 400 : 160 } }}
                                value={formState.fields.description.value}
                                label={localize(messages.descriptionLabel)}
                                onChange={(_, value) => updateFormState({ description: value })}
                            />
                        }
                    />
                </Stack>

                <Stack tokens={{ childrenGap: 15 }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <Stack horizontal verticalAlign="center">
                            <LuisText variant="mediumPlus" fontWeight={600}>
                                {localize(messages.oosThresholdLabel)}
                            </LuisText>

                            <CalloutButton content={localize(messages.oosThresholdTooltip)} />
                        </Stack>
                        <LuisText styles={{ root: { fontStyle: 'italic' } }}>
                            ({localize(messages.experimental)}-{' '}
                            <ExternalLink as="a" href={feedback}>
                                {localize(messages.feedback)}
                            </ExternalLink>
                            )
                        </LuisText>
                    </Stack>

                    <RBACTooltip
                        hidden={canWrite}
                        styles={rBACTooltipStyles}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="projectDescriptionFieldLanguageToolTip"
                        componentSurroundedByToolTip={
                            <LuisTextField
                                min={0}
                                max={1}
                                type="number"
                                disabled={!canWrite}
                                value={oosThresholdText}
                                label={localize(messages.value)}
                                errorMessage={formState.fields.oosThreshold.errorMessage}
                                styles={{ root: { width: isLargeOrGreater ? 400 : 160 } }}
                                onChange={(_e, value) => {
                                    setOosThresholdText(value);
                                    updateFormState({ oosThreshold: parseFloat(value) });
                                }}
                            />
                        }
                    />
                </Stack>

                <Stack tokens={{ childrenGap: 15 }}>
                    {renderTitles(localize(messages.languageTitle))}

                    {renderLabeledText(localize(messages.primaryLanguageTitle), projectLanguage)}
                    {isConversationProject &&
                        renderLabeledText(
                            localize(messages.multiLingualTitle),
                            <RBACTooltip
                                hidden={canWrite}
                                styles={rBACTooltipStyles}
                                displayedUserRole={displayedUserRole}
                                data-automation-id="multiLingualTitleLanguageToolTip"
                                componentSurroundedByToolTip={
                                    <Toggle
                                        disabled={!canWrite}
                                        checked={formStateDto.isMultiLingual}
                                        onText={localize(messages.enabledToggle)}
                                        offText={localize(messages.notEnabledToggle)}
                                        ariaLabel={localize(messages.multiLingualLabel)}
                                        onChange={(_, checked) => {
                                            logToggleChange(
                                                serviceName,
                                                'Settings',
                                                'MultiLingual',
                                                checked ? 'Enabled' : 'Disabled',
                                                currentProject.id
                                            );
                                            updateFormState({ isMultiLingual: checked });
                                        }}
                                    />
                                }
                            />
                        )}
                </Stack>

                {currentResource && (
                    <Stack tokens={{ childrenGap: 15 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            {renderTitles(localize(messages.azureResourceTitle))}
                            <CalloutButton content={localize(messages.azureResourceDescription)} />
                        </Stack>

                        {renderLabeledText(localize(messages.azureResourceLabel), currentResource.name)}
                        {canListResourceKeys &&
                            renderLabeledText(
                                localize(messages.primaryKeyLabel),
                                <ResourceKeyCell account={currentResource} ariaLabel={localize(messages.viewPrimaryKeyLabel)} />
                            )}
                    </Stack>
                )}

                {isConversationProject && (
                    <Stack tokens={{ childrenGap: 15 }}>
                        {renderTitles(localize(messages.advancedSettingsTitle))}
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            {renderLabeledText(
                                localize(messages.normalizeCasingLabel),
                                <RBACTooltip
                                    hidden={canWrite}
                                    styles={rBACTooltipStyles}
                                    displayedUserRole={displayedUserRole}
                                    data-automation-id="normalizeCasingToolTip"
                                    componentSurroundedByToolTip={
                                        <Toggle
                                            disabled={!canWrite}
                                            checked={formStateDto.isCasingNormalized}
                                            onText={localize(messages.enabledToggle)}
                                            offText={localize(messages.notEnabledToggle)}
                                            ariaLabel={localize(messages.normalizeCasingLabel)}
                                            onChange={(_, checked) => {
                                                logToggleChange(
                                                    serviceName,
                                                    'Settings',
                                                    'casing',
                                                    checked ? 'Enabled' : 'Disabled',
                                                    currentProject.id
                                                );
                                                updateFormState({ isCasingNormalized: checked });
                                            }}
                                        />
                                    }
                                />
                            )}
                            <CalloutButton content={localize(messages.casingDescription)} />
                        </Stack>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            {renderLabeledText(
                                localize(messages.normalizeDiacriticsLabel),
                                <RBACTooltip
                                    hidden={canWrite}
                                    styles={rBACTooltipStyles}
                                    displayedUserRole={displayedUserRole}
                                    data-automation-id="normalizeDiacriticsToolTip"
                                    componentSurroundedByToolTip={
                                        <Toggle
                                            disabled={!canWrite}
                                            checked={formStateDto.isDiacriticsAugmented}
                                            onText={localize(messages.enabledToggle)}
                                            offText={localize(messages.notEnabledToggle)}
                                            ariaLabel={localize(messages.normalizeDiacriticsLabel)}
                                            onChange={(_, checked) => {
                                                logToggleChange(
                                                    serviceName,
                                                    'Settings',
                                                    'diacritics',
                                                    checked ? 'Enabled' : 'Disabled',
                                                    currentProject.id
                                                );
                                                updateFormState({ isDiacriticsAugmented: checked });
                                            }}
                                        />
                                    }
                                />
                            )}
                            <CalloutButton content={localize(messages.diacriticsDescription)} />
                            </Stack>
                    </Stack>
                )}

                {isConversationProject && (
                    <Stack tokens={{ childrenGap: 15, padding: '0 0 25px 0' }}>
                        {renderTitles(localize(messages.orchestrationTitle))}
                        <LuisText>{localize(messages.orchestrationDescription)}</LuisText>
                        <DefaultButton
                            text={localize(messages.goToOrchestration)}
                            styles={{ root: { alignSelf: 'flex-start' } }}
                            onClick={() => {
                                logButtonClick(serviceName, 'Settings', 'GoToOrchestration', null, currentProject.id);
                                history.push(routeTable.orchestration.projectsPage());
                            }}
                        />
                    </Stack>
                )}
            </Stack>
        </Page>
    );
});
